import type { PropsWithChildren } from "react"
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { useReactiveVar } from "@apollo/client"

import {
    OUTFITTER_ASSISTANT_AGENT,
    OUTFITTER_ASSISTANT_HISTORY_CAP,
} from "~components/outfitter-assistant/outfitter-assistant-constants"
import useAiChat from "~utils/hooks/use-ai-chat"
import useExecuteAssistantToolCalls from "~components/outfitter-assistant/use-execute-assistant-tool-calls"
import useOutfitterAssistantContext, {
    pathnameToScreenName,
} from "~utils/hooks/use-outfitter-assistant-context"
import { outfitterAssistantPathnameVar } from "~utils/outfitter-assistant-pathname-var"
import {
    outfitterAssistantHistoryVar,
    type AssistantVisitedPage,
} from "~utils/outfitter-assistant-history-var"
import { useOutfitterContext } from "~contexts/outfitter-provider"
import { captureAssistantPageSnapshot } from "~utils/helpers/assistant-page-snapshot"

type AiChat = ReturnType<typeof useAiChat>
type OutfitterAssistantView = "chat" | "history"

interface OutfitterAssistantContextValue {
    isOpen: boolean
    view: OutfitterAssistantView
    open: () => void
    openWithPrompt: (prompt: string) => void
    close: () => void
    toggle: () => void
    showChat: () => void
    showHistory: () => void
    assistantContext: Record<string, unknown>
    chat: AiChat
}

const OutfitterAssistantContext =
    createContext<OutfitterAssistantContextValue | null>(null)

export function OutfitterAssistantProvider({
    children,
}: Readonly<PropsWithChildren>) {
    const [isOpen, setIsOpen] = useState(false)
    const [view, setView] = useState<OutfitterAssistantView>("chat")
    const assistantContext = useOutfitterAssistantContext()

    useTrackAssistantNavigationHistory()

    // The chat hook is owned here (not inside the panel) so its `messages`
    // survive panel unmounts. Closing and reopening the assistant preserves
    // the conversation for the lifetime of the tab/session.
    const chat = useAiChat({
        aiAgent: OUTFITTER_ASSISTANT_AGENT,
        context: assistantContext,
        getDynamicContext: buildDynamicContext,
        onAfterSend: checkpointAssistantHistory,
    })

    // Executes the client-side side effects for tool calls (navigation,
    // etc.) as soon as the server finishes streaming their outputs. Lives
    // on the provider — not the panel — so closing the assistant doesn't
    // swallow an already-requested navigation.
    useExecuteAssistantToolCalls(chat.messages)

    const open = useCallback(() => setIsOpen(true), [])
    const openWithPrompt = useCallback(
        (prompt: string) => {
            setView("chat")
            setIsOpen(true)
            void chat.sendMessage({ text: prompt })
        },
        [chat]
    )
    const close = useCallback(() => {
        setView("chat")
        setIsOpen(false)
    }, [])
    const toggle = useCallback(() => setIsOpen((prev) => !prev), [])
    const showChat = useCallback(() => setView("chat"), [])
    const showHistory = useCallback(() => setView("history"), [])

    const value = useMemo(
        () => ({
            isOpen,
            view,
            open,
            openWithPrompt,
            close,
            toggle,
            showChat,
            showHistory,
            assistantContext,
            chat,
        }),
        [
            isOpen,
            view,
            open,
            openWithPrompt,
            close,
            toggle,
            showChat,
            showHistory,
            assistantContext,
            chat,
        ]
    )

    return (
        <OutfitterAssistantContext.Provider value={value}>
            {children}
        </OutfitterAssistantContext.Provider>
    )
}

export function useOutfitterAssistant() {
    const context = useMaybeOutfitterAssistant()

    if (!context) {
        throw new Error(
            "useOutfitterAssistant must be used within an OutfitterAssistantProvider"
        )
    }

    return context
}

export function useMaybeOutfitterAssistant() {
    return useContext(OutfitterAssistantContext)
}

function useTrackAssistantNavigationHistory(): void {
    const pathname = useReactiveVar(outfitterAssistantPathnameVar)
    const { outfitterId } = useOutfitterContext()

    useEffect(() => {
        appendAssistantVisit({
            screenName: pathnameToScreenName(pathname),
            pathname,
            outfitterId,
        })
    }, [pathname, outfitterId])
}

function appendAssistantVisit({
    screenName,
    pathname,
    outfitterId,
}: AppendAssistantVisitParams): void {
    const history = outfitterAssistantHistoryVar()
    const last = history[history.length - 1]
    if (last && last.pathname === pathname) return

    const entry: AssistantVisitedPage = {
        screenName,
        pathname,
        visitedAt: Date.now(),
        ...(outfitterId ? { entityIds: { outfitterId } } : {}),
    }

    const next = [...history, entry].slice(-OUTFITTER_ASSISTANT_HISTORY_CAP)
    outfitterAssistantHistoryVar(next)
}

function buildDynamicContext(): Record<string, unknown> {
    return { visualContext: captureAssistantPageSnapshot() }
}

function checkpointAssistantHistory(): void {
    outfitterAssistantHistoryVar([])
}

interface AppendAssistantVisitParams {
    screenName: string
    pathname: string
    outfitterId: string | null | undefined
}
